import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import TaskAssignmentsList from "../../components/maintenance/TaskAssignmentsList";
import {
  fetchTaskAssignments,
  fetchTaskAssignmentsSuccess,
  fetchTaskAssignmentsFailure,
  fetchTaskTemplates,
  fetchTaskTemplatesSuccess,
  fetchTaskTemplatesFailure,
  fetchMachines,
  fetchMachinesSuccess,
  fetchMachinesFailure,
  fetchFacilities,
  fetchFacilitiesSuccess,
  fetchFacilitiesFailure,
  deleteTaskAssignment,
  deleteTaskAssignmentSuccess,
  deleteTaskAssignmentFailure,
  generateAssignmentTasks,
  generateAssignmentTasksSuccess,
  generateAssignmentTasksFailure,
  generateAllTasks,
  generateAllTasksSuccess,
  generateAllTasksFailure,
} from "../../store/maintenance/actions";

const TaskAssignmentsListContainer = ({
  assignments,
  templates,
  machines,
  facilities,
  loading,
  error,
  pages,
  fetchTaskAssignments,
  fetchTaskTemplates,
  fetchMachines,
  fetchFacilities,
  deleteTaskAssignment,
  generateAssignmentTasks,
  generateAllTasks,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({
    template: "",
    machine: "",
    facility: "",
    isActive: "",
  });

  useEffect(() => {
    fetchTaskAssignments(1);
    fetchTaskTemplates(1, null, null, null, true);
    fetchMachines();
    fetchFacilities();
  }, [fetchTaskAssignments, fetchTaskTemplates, fetchMachines, fetchFacilities]);

  const handleFilter = useCallback(
    (filters) => {
      setCurrentFilters(filters);
      setCurrentPage(1);
      fetchTaskAssignments(
        1,
        filters.template || null,
        filters.machine || null,
        filters.facility || null,
        filters.isActive === "" ? null : filters.isActive === "true"
      );
    },
    [fetchTaskAssignments]
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchTaskAssignments(
        page,
        currentFilters.template || null,
        currentFilters.machine || null,
        currentFilters.facility || null,
        currentFilters.isActive === "" ? null : currentFilters.isActive === "true"
      );
    },
    [fetchTaskAssignments, currentFilters]
  );

  const handleDelete = useCallback(
    async (id) => {
      try {
        await deleteTaskAssignment(id);
        fetchTaskAssignments(currentPage);
      } catch (err) {
        console.error("Error deleting assignment:", err);
      }
    },
    [deleteTaskAssignment, fetchTaskAssignments, currentPage]
  );

  const handleGenerateTasks = useCallback(
    async (id) => {
      try {
        await generateAssignmentTasks(id);
        fetchTaskAssignments(currentPage);
      } catch (err) {
        console.error("Error generating tasks:", err);
      }
    },
    [generateAssignmentTasks, fetchTaskAssignments, currentPage]
  );

  const handleGenerateAllTasks = useCallback(
    async () => {
      try {
        await generateAllTasks();
        fetchTaskAssignments(currentPage);
      } catch (err) {
        console.error("Error generating all tasks:", err);
      }
    },
    [generateAllTasks, fetchTaskAssignments, currentPage]
  );

  return (
    <TaskAssignmentsList
      assignments={assignments}
      templates={templates}
      machines={machines}
      facilities={facilities}
      loading={loading}
      error={error}
      pages={pages}
      currentPage={currentPage}
      onFilter={handleFilter}
      onDelete={handleDelete}
      onGenerateTasks={handleGenerateTasks}
      onGenerateAllTasks={handleGenerateAllTasks}
      onPageChange={handlePageChange}
    />
  );
};

const mapStateToProps = (state) => ({
  assignments: state.maintenance.taskAssignments.list,
  templates: state.maintenance.taskTemplates.list,
  machines: state.maintenance.machines.list,
  facilities: state.maintenance.facilities.list,
  loading: state.maintenance.taskAssignments.loading,
  error: state.maintenance.taskAssignments.error,
  pages: state.maintenance.taskAssignments.pages,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTaskAssignments: (page, template, machine, facility, isActive) => {
    dispatch(fetchTaskAssignments(page, template, machine, facility, isActive)).payload.then(
      (response) => {
        dispatch(fetchTaskAssignmentsSuccess(response.data));
      },
      (error) => {
        dispatch(fetchTaskAssignmentsFailure(error.response?.data || error));
      }
    );
  },
  fetchTaskTemplates: (page, taskType, targetType, frequency, isActive) => {
    dispatch(fetchTaskTemplates(page, taskType, targetType, frequency, isActive)).payload.then(
      (response) => {
        dispatch(fetchTaskTemplatesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchTaskTemplatesFailure(error.response?.data || error));
      }
    );
  },
  fetchMachines: () => {
    dispatch(fetchMachines()).payload.then(
      (response) => {
        dispatch(fetchMachinesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchMachinesFailure(error.response?.data || error));
      }
    );
  },
  fetchFacilities: () => {
    dispatch(fetchFacilities()).payload.then(
      (response) => {
        dispatch(fetchFacilitiesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchFacilitiesFailure(error.response?.data || error));
      }
    );
  },
  deleteTaskAssignment: (id) => {
    return dispatch(deleteTaskAssignment(id)).payload.then(
      () => {
        dispatch(deleteTaskAssignmentSuccess(id));
      },
      (error) => {
        dispatch(deleteTaskAssignmentFailure(error.response?.data || error));
        throw error;
      }
    );
  },
  generateAssignmentTasks: (id) => {
    return dispatch(generateAssignmentTasks(id)).payload.then(
      (response) => {
        dispatch(generateAssignmentTasksSuccess(response.data));
      },
      (error) => {
        dispatch(generateAssignmentTasksFailure(error.response?.data || error));
        throw error;
      }
    );
  },
  generateAllTasks: () => {
    return dispatch(generateAllTasks()).payload.then(
      (response) => {
        dispatch(generateAllTasksSuccess(response.data));
      },
      (error) => {
        dispatch(generateAllTasksFailure(error.response?.data || error));
        throw error;
      }
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(TaskAssignmentsListContainer);
